/* global google */
// import * as fresults from "./mockResults.json";

// This came from the existing site's source code, not 100% sure it's
// necessary. If there's a better way to load scripts, we should use that.
async function loadScript(src, attrs, useDefer = false) {
  return new Promise((resolve, reject) => {
    if (!document.querySelector(`head > script[src="${src}"]`)) {
      const script = document.createElement("script");
      script.src = src;
      if (attrs) {
        for (const attr in attrs) {
          script.setAttribute(attr, attrs[attr]);
        }
      }
      if (useDefer) {
        script.async = true;
        script.defer = true;
      }
      script.onload = resolve;
      script.onerror = reject;
      document.head.append(script);
    } else {
      resolve();
    }
  });
}

// Loads the Google Maps JavaScript API asynchronously
async function loadGoogleMaps(apiKey) {
  console.log("Loading Google Maps API..."); // eslint-disable-line no-console
  if (!window.google || !window.google.maps) {
    const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=marker,places,geometry`;
    const existingScript = document.querySelector(
      `head > script[src^="${scriptUrl}"]`,
    );

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = scriptUrl;
      script.async = true; // Force async
      script.defer = true; // Force defer
      document.head.appendChild(script);

      return new Promise((resolve, reject) => {
        script.onload = resolve;
        script.onerror = reject;
      });
    }
  }
  return Promise.resolve();
}

// Loads MarkerClusterer library (UMD version for compatibility)
async function loadGoogleClusterer() {
  const scriptUrl =
    "https://unpkg.com/@googlemaps/markerclusterer@2.0.15/dist/index.umd.js";
  return loadScript(scriptUrl);
}

// Unused in this test code, but this is how they're fetching the office listings
// on the live site. We can probably use some of this logic for our own calls,
// since the API endpoints will remain the same.
const getOfficeListings = async params => {
  if (!params.limit) {
    params.limit = 1000;
  }

  if (!params.agent_type) {
    params.agent_type = ["I", "E1", "E2", "S1", "S2"];
  }

  const url = `/Office/Listings/v2?${new URLSearchParams(params)}`;

  let results = null;

  try {
    const response = await esbFetch(url, {
      method: "GET",
    });

    if (response.ok) {
      const {
        StewartOffices: { StewartOffice },
      } = await response.json();
      results = StewartOffice.map(office => {
        let phone = office.Phone;
        let fax = office.Fax;

        if (phone && phone.length === 10) {
          phone = `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
        }
        if (fax && fax.length === 10) {
          fax = `(${fax.slice(0, 3)}) ${fax.slice(3, 6)}-${fax.slice(6)}`;
        }

        return {
          ...office,
          Phone: phone,
          Fax: fax,
        };
      });
    } else {
      const error = await response.text();
      throw new Error(error);
    }
  } catch (e) {
    console.log(e); // eslint-disable-line no-console
  }

  return results;
};

export default class LocatorResultsOfficeSearchHandler {
  constructor() {
    // Store localized placeholders for UI text
    this.blockPlaceholders = {};

    // Google Maps objects
    this.map = null;
    this.mapClusterLayer = null; // Holds the MarkerClusterer instance
    this.mapInfobox = null;

    // Search results and state tracking
    this.results = [];
    this.filter = "";
    this.view = "";
    this.page = -1;
    this.MarkerClusterer = null; // Will hold the clusterer class
  }

  decorate(block) {
    // fresults is a mock data JSON file, taken from a real API response.
    // Will need to add code to make API calls at the right time, and
    // funnel those results into these methods.
    // `getOfficeListings` in this file for an example of how it's done on the live site.
    loadGoogleClusterer();
    this.populateMapMarkers(fresults.StewartOffices.StewartOffice);
  }

  // Initialize Google Map and MarkerClusterer
  async populateMapMarkers(filteredResults) {
    if (!this.map) {
      const mapContainer = document.querySelector(
        ".locator-results-map-container",
      );
      // Google Maps API key used by existing site. Should be able to
      // continue using this, at least for now.
      await loadGoogleMaps("AIzaSyBqsinAy2Itg1XuDwn1nI10gtxaf_9TEpU");
      await loadGoogleClusterer();
      if (window.markerClusterer && window.google && mapContainer) {
        this.MarkerClusterer = window.markerClusterer.MarkerClusterer;
        this.map = new window.google.maps.Map(mapContainer, {
          center: { lat: 39.8283, lng: -98.5795 },
          zoom: 5,
          mapId: "df8d2d0783111f6decedefb3",
          disableDefaultUI: true,
          minZoom: 3,
          maxZoom: 15,
        });
        this.mapInfobox = new window.google.maps.InfoWindow();
        this.updateMapMarkers(filteredResults);
      }
    }
  }

  // Place markers and apply clustering
  updateMapMarkers(filteredResults) {
    const markers = [];
    const bounds = new google.maps.LatLngBounds();

    filteredResults.forEach(item => {
      const { Latitude, Longitude, AgentType } = item;
      if (Latitude && Longitude) {
        const position = {
          lat: parseFloat(Latitude),
          lng: parseFloat(Longitude),
        };
        bounds.extend(position);
        const color = AgentType === "I" ? "purple" : "teal";

        // Create styled marker with larger size and white border for better visibility
        const marker = new google.maps.Marker({
          position, // Set marker position using latitude and longitude
          map: this.map, // Attach marker to the map instance
          title: item.OfficeName || "Location", // Set tooltip/title for the marker
          icon: {
            path: google.maps.SymbolPath.CIRCLE, // Use circle shape for a modern look
            scale: 10, // Slightly larger size (default was 8)
            fillColor: color, // Use dynamic color based on AgentType (teal or purple)
            fillOpacity: 1, // Fully opaque fill for strong visibility
            strokeColor: "#FFFFFF", // Add a clean white border around the marker
            strokeWeight: 2, // Set border thickness to 2px
          },
        });

        // Attach info window to marker
        marker.addListener("click", async () => {
          const content = await ui.createMapMarkerInfoBox(item, block);
          this.mapInfobox.setContent(content.outerHTML);
          this.mapInfobox.setPosition(position);
          this.mapInfobox.open(this.map, marker);
        });
        markers.push(marker);
      }
    });

    // Apply clustering to markers
    if (this.mapClusterLayer) {
      this.mapClusterLayer.clearMarkers();
    }

    this.mapClusterLayer = new this.MarkerClusterer({
      map: this.map,
      markers,
      algorithmOptions: { maxZoom: 5 },
      renderer: {
        render({ count, position }) {
          // Create a custom cluster icon with Stewart color (vinotinto) and transparency
          const div = document.createElement("div");
          div.style.backgroundColor = "rgba(0, 91, 110, 0.8)"; // vinotinto + transparency
          div.style.color = "white";
          div.style.borderRadius = "50%";
          div.style.width = `${40 + Math.log(count) * 10}px`; // size grows with count
          div.style.height = div.style.width;
          div.style.display = "flex";
          div.style.alignItems = "center";
          div.style.justifyContent = "center";
          div.style.fontSize = "14px";
          div.style.fontWeight = "bold";
          div.style.boxShadow = "0 0 10px rgba(0,0,0,0.3)";
          div.textContent = count;

          return new google.maps.marker.AdvancedMarkerElement({
            position,
            content: div,
          });
        },
      },
    });

    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds);
      google.maps.event.addListenerOnce(this.map, "bounds_changed", () => {
        const currentZoom = this.map.getZoom();
        if (currentZoom > 10) this.map.setZoom(10);
        if (currentZoom < 4) this.map.setZoom(4);
      });
    }
  }
}
